import {
  fetchPlayerBets,
  fetchBetDetails,
  fetchPlayerSeeds,
  verifyFairness,
  updateClientSeed,
} from '@services/playerService'
import { fetchRoundInfo, fetchProvablyFair } from '@services/crashService'
import { fetchFreebets, getFreebetsCountOnStatus } from '@services/freebetService'
import {
  getTournaments,
  getTournamentsHistory,
  getTournamentActiveLeaderboard,
  getTournamentLeaderboardHistory,
  getTournamentPrizes,
  tournamentOptIn,
} from '@services/tournamentService'
import { getBuyBonusPackages, buyBonusPackage } from '@services/buyBonusService'
import {
  getCurrentJackpotForBrand,
  getCurrentJackpotForBrandUserSpecific,
  getUserAllTicketsForJackpot,
  getTopCurrentJackpotContributions,
  getPrizeDistributionDetailsForJackpot,
  getUserContributionsForJackpot,
  getUserJackpotHistory,
  getUserJackpotHistoryResults,
} from '@services/jackpotService'

import { toBetsView, toBetDetailsView } from './mappers/betsMapper'
import {
  toSeedsView,
  toVerifyFairnessView,
  toRoundInfoView,
  toProvablyFairView,
} from './mappers/provablyFairMapper'
import { toFreebetsView, toFreebetsCountView } from './mappers/freebetsMapper'
import {
  toTournamentsView,
  toTournamentsHistoryView,
  toLeaderboardView,
  toLeaderboardHistoryView,
  toTournamentPrizesView,
} from './mappers/tournamentsMapper'
import { toBuyBonusPackagesView } from './mappers/buyBonusMapper'
import {
  toCurrentJackpotView,
  toJackpotTicketsView,
  toTopContributionsView,
  toPrizeDistributionView,
  toContributionsView,
  toJackpotHistoryView,
  toJackpotHistoryResultsView,
} from './mappers/jackpotMapper'

// Read operations. Each entry: { service, mapper, key(params), enabled?(params) }.
// `key` builds the TanStack cache key; globals are excluded because they never
// change within a session (a new session = a fresh launch). `enabled` gates lazy
// / on-demand queries that need a local id before they should run.
export const QUERY_REGISTRY = {
  // --- Bets ---
  'bets.list': {
    service: fetchPlayerBets,
    mapper: toBetsView,
    key: (params) => ['bets', params],
  },
  'betDetails.get': {
    service: fetchBetDetails,
    mapper: toBetDetailsView,
    key: (params) => ['betDetails', params.transactionId],
    enabled: (params) => !!params.transactionId,
  },

  // --- Provably fair ---
  'provablyFair.seeds': {
    service: fetchPlayerSeeds,
    mapper: toSeedsView,
    key: () => ['provablyFair', 'seeds'],
  },
  'provablyFair.verify': {
    service: verifyFairness,
    mapper: toVerifyFairnessView,
    key: (params) => ['provablyFair', 'verify', params],
    enabled: (params) => !!params.hash && !!params.seed,
  },
  'provablyFair.roundInfo': {
    service: fetchRoundInfo,
    mapper: toRoundInfoView,
    key: (params) => ['provablyFair', 'roundInfo', params.roundId],
    enabled: (params) => !!params.roundId,
  },
  // Live seed commitment. Keyed by game/mode because the algorithm parameters
  // (house edge, raw space, game seed) are per-game.
  'provablyFair.active': {
    service: fetchProvablyFair,
    mapper: toProvablyFairView,
    key: (params) => ['provablyFair', 'active', params.gameCode, params.gameMode],
  },

  // --- Freebets ---
  'freebets.list': {
    service: fetchFreebets,
    mapper: toFreebetsView,
    key: (params) => ['freebets', params],
  },
  'freebets.count': {
    service: getFreebetsCountOnStatus,
    mapper: toFreebetsCountView,
    key: (params) => ['freebets', 'count', params],
  },

  // --- Tournaments ---
  'tournaments.list': {
    service: getTournaments,
    mapper: toTournamentsView,
    key: (params) => ['tournaments', params],
  },
  'tournaments.history': {
    service: getTournamentsHistory,
    mapper: toTournamentsHistoryView,
    key: (params) => ['tournaments', 'history', params],
  },
  'tournaments.leaderboard': {
    service: getTournamentActiveLeaderboard,
    mapper: toLeaderboardView,
    key: (params) => ['tournaments', 'leaderboard', params],
    enabled: (params) => !!params.tournamentId,
  },
  'tournaments.leaderboardHistory': {
    service: getTournamentLeaderboardHistory,
    mapper: toLeaderboardHistoryView,
    key: (params) => ['tournaments', 'leaderboardHistory', params],
    enabled: (params) => !!params.tournamentId,
  },
  'tournaments.prizes': {
    service: getTournamentPrizes,
    mapper: toTournamentPrizesView,
    key: (params) => ['tournaments', 'prizes', params.tournamentId],
    enabled: (params) => !!params.tournamentId,
  },

  // --- Buy bonus ---
  'buyBonus.packages': {
    service: getBuyBonusPackages,
    mapper: toBuyBonusPackagesView,
    key: () => ['buyBonus', 'packages'],
  },

  // --- Jackpot ---
  'jackpot.current': {
    service: getCurrentJackpotForBrand,
    mapper: toCurrentJackpotView,
    key: () => ['jackpot', 'current'],
  },
  'jackpot.currentUserSpecific': {
    service: getCurrentJackpotForBrandUserSpecific,
    mapper: toCurrentJackpotView,
    key: () => ['jackpot', 'currentUserSpecific'],
  },
  'jackpot.tickets': {
    service: getUserAllTicketsForJackpot,
    mapper: toJackpotTicketsView,
    key: (params) => ['jackpot', 'tickets', params.jackpotId],
    enabled: (params) => !!params.jackpotId,
  },
  'jackpot.topContributions': {
    service: getTopCurrentJackpotContributions,
    mapper: toTopContributionsView,
    key: () => ['jackpot', 'topContributions'],
  },
  'jackpot.prizeDistribution': {
    service: getPrizeDistributionDetailsForJackpot,
    mapper: toPrizeDistributionView,
    key: (params) => ['jackpot', 'prizeDistribution', params.jackpotId],
    enabled: (params) => !!params.jackpotId,
  },
  'jackpot.contributions': {
    service: getUserContributionsForJackpot,
    mapper: toContributionsView,
    key: (params) => ['jackpot', 'contributions', params.jackpotId],
    enabled: (params) => !!params.jackpotId,
  },
  'jackpot.history': {
    service: getUserJackpotHistory,
    mapper: toJackpotHistoryView,
    key: () => ['jackpot', 'history'],
  },
  'jackpot.historyResults': {
    service: getUserJackpotHistoryResults,
    mapper: toJackpotHistoryResultsView,
    key: (params) => ['jackpot', 'historyResults', params.jackpotId],
    enabled: (params) => !!params.jackpotId,
  },
}

// Write operations. Each entry: { service, invalidates? }.
// `invalidates` is a list of query-key prefixes refetched on success — TanStack
// matches by prefix, so ['freebets'] refreshes both the list and the count.
export const MUTATION_REGISTRY = {
  'provablyFair.updateClientSeed': {
    service: updateClientSeed,
    invalidates: [['provablyFair', 'seeds']],
  },
  'buyBonus.purchase': {
    service: buyBonusPackage,
    invalidates: [['freebets'], ['buyBonus', 'packages']],
  },
  'tournaments.optIn': {
    service: tournamentOptIn,
    invalidates: [['tournaments']],
  },
}
